import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import { Elements } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'

import SearchField from '../../../../FormLegacy/FormFieldsV2/SearchField'
import FeaturedTable from '../../../../FeaturedTable/FeaturedTable'
import PaginationFooter from '../../../../PaginationFooter/PaginationFooter'
import ConfirmPopup from '../../../../Popups/ConfirmPopup'
import RewardsClientActivityDetailsModal from '../../RewardsClientActivityDetailsModal/RewardsClientActivityDetailsModal'
import RewardsLoyaltyProfileModal from '../../RewardsLoyaltyProfileModal/RewardsLoyaltyProfileModal'
import RewardsActivityEvidenceModal from '../../RewardsActivityEvidenceModal/RewardsActivityEvidenceModal'
import RewardsAddLoyaltyActivityModal from '../../RewardsAddLoyaltyActivityModal/RewardsAddLoyaltyActivityModal'
import RewardsManageActivitiesModal from '../../RewardsManageActivitiesModal/RewardsManageActivitiesModal'
import RewardsAddFundsModal from '../../RewardsAddFundsModal/RewardsAddFundsModal'
import {
  selectLoyaltyDashboard,
  fetchLoyaltyDashboard,
  cancelDashboardActivityPoints,
  declineDashboardActivity,
  approveDashboardActivity,
} from '../../../../../reducers/loyaltyReducer'
import { loyaltyTableColumns, getLoyaltyTabs, renderEvidence, stripePromise } from '../../helpers'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'

const LoyaltyTable = ({
  dashboard = [],
  building = {},
  fetchLoyaltyDashboard = () => {},
  cancelDashboardActivityPoints = () => {},
  declineDashboardActivity = () => {},
  approveDashboardActivity = () => {},
}) => {
  const { t } = useTranslation(['rewards-loyalty', 'rewards-dashboard', 'common'])
  const loyaltyTabs = getLoyaltyTabs(t)
  const [search, setSearch] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [selectedTab, setSelectedTab] = useState('')
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [approveActivity, setApproveActivity] = useState(null)
  const [declineActivity, setDeclineActivity] = useState(null)
  const [cancelPointsActivity, setCancelPointsActivity] = useState(null)
  const [activityDetails, setActivityDetails] = useState(null)
  const [loyaltyProfile, setLoyaltyProfile] = useState(null)
  const [reviewActivity, setReviewActivity] = useState(null)
  const [showAddActivity, setShowAddActivity] = useState(false)
  const [addActivityClient, setAddActivityClient] = useState(null)
  const [showMangeActivities, setShowManageActivities] = useState(false)
  const [showNoFunds, setShowNoFunds] = useState(false)
  const [isShowAddFunds, setShowAddFunds] = useState(false)
  const [photoIndexToDisplay, setPhotoIndexToDisplay] = useState(null)
  const { timezone, rewardsBalance } = building
  const { displaySnackbar } = useSnackbar()

  const onSearchChange = (e) => {
    setPageNumber(1)
    setSearch(e.target.value)
  }

  const onTabChange = (tabFilter) => {
    setPageNumber(1)
    setSelectedTab(tabFilter)
  }

  const onPageChange = (number) => {
    setPageNumber(1)
    setShowOnPage(number)
  }

  const fetchDashboard = async (query) => {
    setLoading(true)

    if (query === undefined) {
      query = search
    }

    const requestData = {
      buildingId: building.id,
      page: query ? 1 : pageNumber,
      limit: showOnPage,
      type: selectedTab && selectedTab.value ? selectedTab.value : undefined,
      query,
    }

    await fetchLoyaltyDashboard(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch((error) => console.error(error.message))
    setLoading(false)
  }

  const onApprove = () => {
    const { originalType, rewardValue } = approveActivity
    if (originalType === 'reward-claimed' && rewardsBalance < rewardValue / 100) {
      setReviewActivity(null)
      setApproveActivity(null)
      setShowNoFunds(true)
    } else {
      onApproveActivity()
    }
  }

  const onApproveActivity = async () => {
    const { originalType } = approveActivity
    const isReward = originalType === 'reward-claimed'
    try {
      await approveDashboardActivity(approveActivity.id)
      displaySnackbar(
        isReward
          ? t('rewards-loyalty:loyaltyTable.rewardApprovedSuccess')
          : t('rewards-loyalty:loyaltyTable.pointsApprovedSuccess', {
              points: approveActivity.points,
              activityName: approveActivity.activityName,
              name: approveActivity.name,
            }),
      )
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(
        isReward
          ? t('rewards-loyalty:loyaltyTable.rewardApprovedError')
          : t('rewards-loyalty:loyaltyTable.activityApprovedError'),
        true,
      )
    } finally {
      setReviewActivity(null)
      setApproveActivity(null)
    }
  }

  const onDeclineActivity = async () => {
    const { originalType } = declineActivity
    const isReward = originalType === 'reward-claimed'
    try {
      await declineDashboardActivity(declineActivity.id)
      displaySnackbar(
        isReward
          ? t('rewards-loyalty:loyaltyTable.rewardDeclinedSuccess')
          : t('rewards-loyalty:loyaltyTable.activityDeclinedSuccess'),
      )
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(
        isReward
          ? t('rewards-loyalty:loyaltyTable.rewardDeclinedError')
          : t('rewards-loyalty:loyaltyTable.activityDeclinedError'),
        true,
      )
    } finally {
      setReviewActivity(null)
      setDeclineActivity(null)
    }
  }

  const onCancelPoints = async () => {
    try {
      await cancelDashboardActivityPoints(cancelPointsActivity.id)
      displaySnackbar(
        t('rewards-loyalty:loyaltyTable.pointsCanceledSuccess', {
          points: cancelPointsActivity.points,
          activityName: cancelPointsActivity.activityName,
        }),
      )
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('rewards-loyalty:loyaltyTable.pointsCanceledError'), true)
    } finally {
      setCancelPointsActivity(null)
    }
  }

  const onOpenImageModal = (index) => setPhotoIndexToDisplay(index)

  const debouncedFetch = useCallback(debounce(fetchDashboard, 800), [showOnPage, selectedTab])

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debouncedFetch(search))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [search])

  useEffect(() => {
    if (building) {
      fetchDashboard()
    }
  }, [pageNumber, showOnPage, selectedTab, building])

  const cautionActivityBody = () => {
    const { originalType, name, activityName, rewardName } = approveActivity || declineActivity
    const text = approveActivity
      ? originalType === 'reward-claimed'
        ? t('rewards-loyalty:loyaltyTable.approveRedeemingConfirm', { rewardName, name })
        : t('rewards-loyalty:loyaltyTable.approveActivityConfirm', { activityName, name })
      : originalType === 'reward-claimed'
        ? t('rewards-loyalty:loyaltyTable.declineRedeemingConfirm', { name, rewardName })
        : t('rewards-loyalty:loyaltyTable.declineActivityConfirm', { name, activityName })

    const activity = approveActivity || declineActivity

    return (
      <div className="caution-activity-body">
        <span>{text}</span>
        {originalType !== 'reward-claimed' && (
          <div className="caution-activity-evidence">
            <span>{t('rewards-loyalty:evidence.activityCompletionEvidence')}</span>
            {renderEvidence(activity, onOpenImageModal, photoIndexToDisplay, t)}
          </div>
        )}
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="rewards-container">
        <div className="rewards-table">
          <div className="rewards-table__wrap">
            <div className="rewards-table__filters">
              <div className="rewards-table__filters__group">
                {loyaltyTabs.map((tabFilter) => (
                  <button
                    className={`rewards-filter-item ${
                      selectedTab && tabFilter.id === selectedTab.id ? 'active' : ''
                    }`}
                    key={tabFilter.id}
                    onClick={() => onTabChange(tabFilter)}
                    disabled={isLoading}
                  >
                    <p style={{ display: 'inline-block' }}>{tabFilter.text} </p>
                  </button>
                ))}
              </div>
              <div className="rewards-table__filters__group">
                <div className={'rewards-table__filters__search'}>
                  <SearchField
                    placeholder={t('rewards-dashboard:leaderBoard.searchPlaceholder')}
                    value={search}
                    onChange={onSearchChange}
                  />
                </div>
                <button
                  className="button button-black"
                  onClick={() => setShowManageActivities(true)}
                  disabled={isLoading}
                  style={{ marginRight: 15 }}
                >
                  {t('rewards-loyalty:loyaltyTable.manageActivitiesButton')}
                </button>
                <button
                  className="button button-black"
                  onClick={() => setShowAddActivity(true)}
                  disabled={isLoading}
                >
                  {t('rewards-loyalty:loyaltyTable.addActivityButton')}
                </button>
              </div>
            </div>
            <div className={`ctp-loyalty-list ${!isLoading ? '' : 'is-loading'}`}>
              {!isLoading && (
                <React.Fragment>
                  {dashboard.length > 0 && building ? (
                    <FeaturedTable
                      columns={loyaltyTableColumns({
                        timezone,
                        onApprove: setApproveActivity,
                        onDecline: setDeclineActivity,
                        onCancelPoints: setCancelPointsActivity,
                        onDetails: setActivityDetails,
                        onLoyaltyProfile: setLoyaltyProfile,
                        onReview: setReviewActivity,
                        t,
                      })}
                      data={dashboard}
                    />
                  ) : (
                    t('rewards-dashboard:leaderBoard.noResultsYet')
                  )}
                </React.Fragment>
              )}
            </div>
            {dashboard.length > 0 && (
              <PaginationFooter
                showOnPage={showOnPage}
                pageNumber={pageNumber}
                paginationItemsCount={paginationItemsCount}
                setSiteLoading={setLoading}
                setShowOnPage={onPageChange}
                setPageNumber={setPageNumber}
              />
            )}
          </div>
        </div>
      </div>
      {approveActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:loyaltyTable.approveButton')}
          isOpen={!!approveActivity}
          body={cautionActivityBody()}
          cancelCallback={() => setApproveActivity(null)}
          confirmCallback={onApprove}
          className={'is-caution'}
        />
      )}
      {declineActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:loyaltyTable.declineButton')}
          isOpen={!!declineActivity}
          body={cautionActivityBody()}
          cancelCallback={() => setDeclineActivity(null)}
          confirmCallback={onDeclineActivity}
          className={'is-caution'}
        />
      )}
      {cancelPointsActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('rewards-loyalty:loyaltyTable.yesCancelPointsButton')}
          isOpen={!!cancelPointsActivity}
          body={t('rewards-loyalty:loyaltyTable.cancelPointsConfirm', {
            points: cancelPointsActivity.points,
            activityName: cancelPointsActivity.activityName,
            name: cancelPointsActivity.name,
          })}
          cancelCallback={() => setCancelPointsActivity(null)}
          confirmCallback={onCancelPoints}
        />
      )}
      {loyaltyProfile && (
        <RewardsLoyaltyProfileModal
          building={building}
          client={loyaltyProfile}
          onClose={() => setLoyaltyProfile(null)}
          setActivityDetails={setActivityDetails}
          onAddActivity={() => {
            setShowAddActivity(true)
            setAddActivityClient(loyaltyProfile)
          }}
        />
      )}
      {reviewActivity && (
        <RewardsActivityEvidenceModal
          activity={reviewActivity}
          onClose={() => setReviewActivity(null)}
          onApprove={() => setApproveActivity(reviewActivity)}
          onDecline={() => setDeclineActivity(reviewActivity)}
        />
      )}
      {activityDetails && (
        <RewardsClientActivityDetailsModal
          building={building}
          activity={activityDetails}
          onClose={() => setActivityDetails(null)}
        />
      )}
      {showAddActivity && (
        <RewardsAddLoyaltyActivityModal
          building={building}
          onClose={() => {
            setShowAddActivity(false)
            setAddActivityClient(null)
          }}
          client={addActivityClient}
        />
      )}
      {showMangeActivities && (
        <RewardsManageActivitiesModal
          building={building}
          onClose={() => setShowManageActivities(false)}
        />
      )}
      {showNoFunds && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:loyaltyTable.addFundsButton')}
          isOpen={showNoFunds}
          body={t('rewards-loyalty:loyaltyTable.insufficientBalance')}
          cancelCallback={() => setShowNoFunds(null)}
          confirmCallback={() => {
            setShowNoFunds(false)
            setShowAddFunds(true)
          }}
        />
      )}
      {isShowAddFunds && (
        <Elements stripe={stripePromise}>
          <RewardsAddFundsModal
            onClose={() => setShowAddFunds(false)}
            selectedBuilding={building}
          />
        </Elements>
      )}
    </React.Fragment>
  )
}

LoyaltyTable.propTypes = {
  fetchLoyaltyDashboard: PropTypes.func,
  cancelDashboardActivityPoints: PropTypes.func,
  declineDashboardActivity: PropTypes.func,
  approveDashboardActivity: PropTypes.func,
  dashboard: PropTypes.array,
  building: PropTypes.object,
}

export default connect(
  (state) => ({
    dashboard: selectLoyaltyDashboard(state),
  }),
  {
    selectLoyaltyDashboard,
    fetchLoyaltyDashboard,
    cancelDashboardActivityPoints,
    declineDashboardActivity,
    approveDashboardActivity,
  },
)(LoyaltyTable)
